/**
 * Demo data for hero titles in the Vision Prototype.
 * This supplements real S3 parquet data with hardcoded values
 * for demo-critical metrics that aren't available in the pipeline.
 * 
 * These 8 titles have matching parrot_ids in the local sample data.
 */

export interface SeasonBreakdown {
  season: number;
  episodes: number;
  releaseDate: string;
  releasePattern: 'weekly' | 'all-at-once';
  revenueUsd: number;
  perEpisodeUsd: number;
  peakRank: number;
}

export interface DemoTitleData {
  parrotId: string;
  title: string;
  platform: string;
  qoqTrendPct: number;
  qoqDirection: 'up' | 'down' | 'flat';
  seasons?: SeasonBreakdown[];
  demandTier?: 'exceptional' | 'outstanding' | 'good' | 'average' | 'low';
  narrativeInsight?: string;
}

/**
 * Hero titles with demo data overlays.
 * Keyed by parrot_id (UUID) for quick lookup.
 */
export const DEMO_TITLES: Record<string, DemoTitleData> = {
  // Netflix
  '065ecb7f-5bef-4001-aba9-20900708af22': {
    parrotId: '065ecb7f-5bef-4001-aba9-20900708af22',
    title: 'Stranger Things',
    platform: 'Netflix',
    qoqTrendPct: 2.4,
    qoqDirection: 'up',
    demandTier: 'exceptional',
    narrativeInsight: 'Netflix\'s flagship acquisition driver, consistently ranking #1 with each season release.',
    seasons: [
      { season: 1, episodes: 8, releaseDate: '2016-07-15', releasePattern: 'all-at-once', revenueUsd: 95_000_000, perEpisodeUsd: 11_875_000, peakRank: 1 },
      { season: 2, episodes: 9, releaseDate: '2017-10-27', releasePattern: 'all-at-once', revenueUsd: 142_000_000, perEpisodeUsd: 15_777_778, peakRank: 1 },
      { season: 3, episodes: 8, releaseDate: '2019-07-04', releasePattern: 'all-at-once', revenueUsd: 187_000_000, perEpisodeUsd: 23_375_000, peakRank: 1 },
      { season: 4, episodes: 9, releaseDate: '2022-05-27', releasePattern: 'all-at-once', revenueUsd: 268_000_000, perEpisodeUsd: 29_777_778, peakRank: 1 },
      { season: 5, episodes: 8, releaseDate: '2025-07-04', releasePattern: 'all-at-once', revenueUsd: 155_000_000, perEpisodeUsd: 19_375_000, peakRank: 1 },
    ],
  },

  // Prime Video
  '81617901-2430-4f98-bb3b-f5aa9fb7758f': {
    parrotId: '81617901-2430-4f98-bb3b-f5aa9fb7758f',
    title: 'The Boys',
    platform: 'Prime Video',
    qoqTrendPct: 3.1,
    qoqDirection: 'up',
    demandTier: 'exceptional',
    narrativeInsight: 'Prime Video\'s top acquisition driver. Superhero genre with R-rated differentiation.',
    seasons: [
      { season: 1, episodes: 8, releaseDate: '2019-07-26', releasePattern: 'all-at-once', revenueUsd: 145_000_000, perEpisodeUsd: 18_125_000, peakRank: 1 },
      { season: 2, episodes: 8, releaseDate: '2020-09-04', releasePattern: 'weekly', revenueUsd: 187_000_000, perEpisodeUsd: 23_375_000, peakRank: 1 },
      { season: 3, episodes: 8, releaseDate: '2022-06-03', releasePattern: 'weekly', revenueUsd: 212_000_000, perEpisodeUsd: 26_500_000, peakRank: 1 },
      { season: 4, episodes: 8, releaseDate: '2024-06-13', releasePattern: 'weekly', revenueUsd: 198_000_000, perEpisodeUsd: 24_750_000, peakRank: 1 },
    ],
  },

  // Disney+
  'a5fd285d-88ce-4156-a41e-94db797e08c7': {
    parrotId: 'a5fd285d-88ce-4156-a41e-94db797e08c7',
    title: 'The Mandalorian',
    platform: 'Disney+',
    qoqTrendPct: -2.1,
    qoqDirection: 'down',
    demandTier: 'exceptional',
    narrativeInsight: 'Disney+ flagship. Franchise anchor with merchandise halo effect.',
    seasons: [
      { season: 1, episodes: 8, releaseDate: '2019-11-12', releasePattern: 'weekly', revenueUsd: 234_000_000, perEpisodeUsd: 29_250_000, peakRank: 1 },
      { season: 2, episodes: 8, releaseDate: '2020-10-30', releasePattern: 'weekly', revenueUsd: 267_000_000, perEpisodeUsd: 33_375_000, peakRank: 1 },
      { season: 3, episodes: 8, releaseDate: '2023-03-01', releasePattern: 'weekly', revenueUsd: 198_000_000, perEpisodeUsd: 24_750_000, peakRank: 1 },
    ],
  },
  '9484dca7-69d0-4da5-a259-838e06842e45': {
    parrotId: '9484dca7-69d0-4da5-a259-838e06842e45',
    title: 'Bluey',
    platform: 'Disney+',
    qoqTrendPct: 6.8,
    qoqDirection: 'up',
    demandTier: 'outstanding',
    narrativeInsight: 'Children\'s phenomenon with family co-viewing. Exceptional merchandise potential.',
    seasons: [
      { season: 1, episodes: 52, releaseDate: '2018-10-01', releasePattern: 'weekly', revenueUsd: 45_000_000, perEpisodeUsd: 865_385, peakRank: 4 },
      { season: 2, episodes: 52, releaseDate: '2020-03-17', releasePattern: 'weekly', revenueUsd: 78_000_000, perEpisodeUsd: 1_500_000, peakRank: 2 },
      { season: 3, episodes: 50, releaseDate: '2021-11-22', releasePattern: 'weekly', revenueUsd: 125_000_000, perEpisodeUsd: 2_500_000, peakRank: 1 },
    ],
  },

  // Apple TV+
  '45e814ae-f050-47ec-80cc-9583c94ca000': {
    parrotId: '45e814ae-f050-47ec-80cc-9583c94ca000',
    title: 'Ted Lasso',
    platform: 'Apple TV+',
    qoqTrendPct: -4.2,
    qoqDirection: 'down',
    demandTier: 'good',
    narrativeInsight: 'Apple TV+ flagship. Comedy with exceptional brand affinity.',
    seasons: [
      { season: 1, episodes: 10, releaseDate: '2020-08-14', releasePattern: 'weekly', revenueUsd: 67_000_000, perEpisodeUsd: 6_700_000, peakRank: 1 },
      { season: 2, episodes: 12, releaseDate: '2021-07-23', releasePattern: 'weekly', revenueUsd: 98_000_000, perEpisodeUsd: 8_166_667, peakRank: 1 },
      { season: 3, episodes: 12, releaseDate: '2023-03-15', releasePattern: 'weekly', revenueUsd: 112_000_000, perEpisodeUsd: 9_333_333, peakRank: 1 },
    ],
  },
  '14da002c-7f29-4924-a5d3-f430722cf6ff': {
    parrotId: '14da002c-7f29-4924-a5d3-f430722cf6ff',
    title: 'Severance',
    platform: 'Apple TV+',
    qoqTrendPct: 12.5,
    qoqDirection: 'up',
    demandTier: 'outstanding',
    narrativeInsight: 'Cult hit with massive S2 growth. Highest social engagement on Apple TV+.',
    seasons: [
      { season: 1, episodes: 9, releaseDate: '2022-02-18', releasePattern: 'weekly', revenueUsd: 54_000_000, perEpisodeUsd: 6_000_000, peakRank: 2 },
      { season: 2, episodes: 10, releaseDate: '2025-01-17', releasePattern: 'weekly', revenueUsd: 145_000_000, perEpisodeUsd: 14_500_000, peakRank: 1 },
    ],
  },

  // Paramount+
  'a5cec87f-f12d-4ac2-b821-e60bab9444f0': {
    parrotId: 'a5cec87f-f12d-4ac2-b821-e60bab9444f0',
    title: 'Yellowstone',
    platform: 'Paramount+',
    qoqTrendPct: -1.8,
    qoqDirection: 'down',
    demandTier: 'outstanding',
    narrativeInsight: 'Linear crossover success. Strong in non-coastal US markets.',
    seasons: [
      { season: 1, episodes: 9, releaseDate: '2018-06-20', releasePattern: 'weekly', revenueUsd: 34_000_000, perEpisodeUsd: 3_777_778, peakRank: 5 },
      { season: 2, episodes: 10, releaseDate: '2019-06-19', releasePattern: 'weekly', revenueUsd: 56_000_000, perEpisodeUsd: 5_600_000, peakRank: 3 },
      { season: 3, episodes: 10, releaseDate: '2020-06-21', releasePattern: 'weekly', revenueUsd: 89_000_000, perEpisodeUsd: 8_900_000, peakRank: 2 },
      { season: 4, episodes: 10, releaseDate: '2021-11-07', releasePattern: 'weekly', revenueUsd: 134_000_000, perEpisodeUsd: 13_400_000, peakRank: 1 },
      { season: 5, episodes: 14, releaseDate: '2022-11-13', releasePattern: 'weekly', revenueUsd: 178_000_000, perEpisodeUsd: 12_714_286, peakRank: 1 },
    ],
  },

  // Hulu
  '9d9f44d3-aa81-4cfc-b1c6-5794f2eadf2d': {
    parrotId: '9d9f44d3-aa81-4cfc-b1c6-5794f2eadf2d',
    title: 'The Bear',
    platform: 'Hulu',
    qoqTrendPct: 7.4,
    qoqDirection: 'up',
    demandTier: 'outstanding',
    narrativeInsight: 'Critical darling. Punches above platform weight with awards buzz.',
    seasons: [
      { season: 1, episodes: 8, releaseDate: '2022-06-23', releasePattern: 'all-at-once', revenueUsd: 78_000_000, perEpisodeUsd: 9_750_000, peakRank: 1 },
      { season: 2, episodes: 10, releaseDate: '2023-06-22', releasePattern: 'all-at-once', revenueUsd: 134_000_000, perEpisodeUsd: 13_400_000, peakRank: 1 },
      { season: 3, episodes: 10, releaseDate: '2024-06-27', releasePattern: 'all-at-once', revenueUsd: 156_000_000, perEpisodeUsd: 15_600_000, peakRank: 1 },
    ],
  },
};

/**
 * Get demo data for a title by parrot_id.
 * Returns undefined if no demo data exists for this title.
 */
export function getDemoData(parrotId: string): DemoTitleData | undefined {
  return DEMO_TITLES[parrotId];
}

/**
 * Get QoQ trend data for the leaderboard.
 * Falls back to simulated data if title not in demo set.
 */
export function getQoQTrend(parrotId: string): { pct: number; direction: 'up' | 'down' | 'flat' } {
  const demo = DEMO_TITLES[parrotId];
  if (demo) {
    return { pct: demo.qoqTrendPct, direction: demo.qoqDirection };
  }
  // Fallback: simulate based on hash of parrotId
  const hash = parrotId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const pct = ((hash % 100) - 50) / 10; // -5% to +5%
  return {
    pct: Math.abs(pct),
    direction: pct >= 0.5 ? 'up' : pct <= -0.5 ? 'down' : 'flat',
  };
}

/**
 * List of parrot_ids for all hero titles (useful for filtering/highlighting).
 */
export const HERO_TITLE_IDS = Object.keys(DEMO_TITLES);
